import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PhoneCodeSelect } from '../lib/phoneCodes';
import { Ghost, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const LANGUAGES = [
  'English',
  'Spanish',
  'Italian',
  'Portuguese',
  'French',
  'German',
  'Japanese',
  'Korean',
  'Mandarin',
  'Egyptian Arabic (Masri)',
];

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const GENDERS = ['Man', 'Woman', 'Prefer not to say'];

function slugify(value) {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function ageFromDate(dateString) {
  if (!dateString) return 0;
  const today = new Date();
  const birth = new Date(dateString);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
}

function StepHeader({ title, text }) {
  return (
    <div className="mb-8">
      <div className="badge-cozy mb-4">Ghostie application</div>
      <h1 className="text-4xl sm:text-5xl font-heading font-semibold mb-3">{title}</h1>
      <p className="text-[#a3a3a3] leading-relaxed max-w-3xl">{text}</p>
    </div>
  );
}

export default function BecomeGhostie() {
  const [cities, setCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { refresh } = useAuth();
  const googleButtonRef = useRef(null);
  const cityInputRef = useRef(null);
  const [f, setF] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    date_of_birth: '',
    email: '',
    password: '',
    phone_code: '+1',
    phone: '',
    city: '',
    living_length: '1',
    living_unit: 'Years',
    language: 'English',
    level: 'C1',
    accepted_terms: false,
  });

  useEffect(() => { api.get('/cities').then(r => setCities(r.data || [])).catch(()=>{}); }, []);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey || !cityInputRef.current) return;
    const initPlaces = () => {
      if (!window.google?.maps?.places || !cityInputRef.current) return;
      const autocomplete = new window.google.maps.places.Autocomplete(cityInputRef.current, { types: ['(cities)'] });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        setF(s => ({ ...s, city: place.formatted_address || place.name || s.city }));
      });
    };
    if (window.google?.maps?.places) { initPlaces(); return; }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = initPlaces;
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    const initialize = () => {
      if (!googleButtonRef.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          try {
            if (!f.accepted_terms) {
              toast.error('Please accept the terms and conditions first.');
              return;
            }
            const { data } = await api.post('/auth/google', { credential, role: 'host', first_name: f.first_name, last_name: f.last_name, gender: f.gender, date_of_birth: f.date_of_birth, phone: `${f.phone_code} ${f.phone}`.trim() });
            await refresh();
            setF(s => ({ ...s, email: data.user?.email || s.email }));
            toast.success('Google account connected. Complete and submit your application.');
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Google connection failed');
          }
        },
      });
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, { theme: 'filled_black', size: 'large', shape: 'pill', width: 320, text: 'continue_with' });
    };
    if (window.google?.accounts?.id) { initialize(); return; }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = initialize;
    document.head.appendChild(script);
  }, [refresh, f.first_name, f.last_name, f.gender, f.date_of_birth, f.phone_code, f.phone, f.accepted_terms]);

  const citySuggestions = useMemo(() => cities.map((c) => `${c.name}, ${c.country}`), [cities]);
  const age = ageFromDate(f.date_of_birth);

  const validateStepOne = () => {
    if (!f.first_name || !f.last_name || !f.gender || !f.date_of_birth || !f.email || !f.phone || !f.city || !f.living_length || !f.language || !f.level) {
      toast.error('Please complete all first-page fields.');
      return false;
    }
    if (!f.accepted_terms) {
      toast.error('Please accept the terms and conditions.');
      return false;
    }
    if (age < 18) {
      toast.error('Ghostie hosts must be at least 18.');
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStepOne()) return;
    const selectedCity = cities.find((c) => `${c.name}, ${c.country}`.toLowerCase() === f.city.toLowerCase());
    const cityId = selectedCity?.city_id || `city_${slugify(f.city || 'external')}`;
    const fullName = `${f.first_name} ${f.last_name}`.trim();
    const languageLine = `${f.language} (${f.level})`;
    const bio = [
      `Gender: ${f.gender}`,
      `Date of birth: ${f.date_of_birth}`,
      `Based in: ${f.city}`,
      `Living there for: ${f.living_length} ${f.living_unit}`,
      'ID verification: pending in host dashboard; ID document will not be stored by Ghostie.',
      'Video reply: pending in host dashboard; production upload should be handled by external video provider/email workflow.',
    ].join('\n');

    setSubmitting(true);
    try {
      await api.post('/hosts/apply', {
        full_name: fullName,
        email: f.email,
        phone: `${f.phone_code} ${f.phone}`,
        password: f.password || undefined,
        gender: f.gender,
        date_of_birth: f.date_of_birth,
        city_id: cityId,
        languages: [languageLine],
        bio,
        topic_expertise: 'Collected in next application review / introductory call.',
        why_become: 'New Ghostie application started from /start.',
        availability_notes: '',
        social_links: '',
        consent_interview: true,
        consent_agreement: true,
        consent_safety: true,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Application failed');
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-24 text-center">
        <Ghost size={64} className="text-[#f97316] mx-auto mb-4 float-anim"/>
        <h1 className="text-3xl font-heading font-semibold mb-3">Your application is in review.</h1>
        <p className="text-[#a3a3a3] leading-relaxed">
          Thanks for applying. A team member will review your application and contact you for your introductory Google Meet call.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
      <StepHeader
        title="Start your Ghostie application"
        text="Tell us who you are and where your local magic lives. ID verification and the quick video reply will happen later inside your Host dashboard."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div><label className="label">First Name</label><input required placeholder="Enter your first name" value={f.first_name} onChange={e=>setF(s=>({...s, first_name:e.target.value}))} className="input-cozy" data-testid="host-first-name"/></div>
        <div><label className="label">Last Name</label><input required placeholder="Enter your last name" value={f.last_name} onChange={e=>setF(s=>({...s, last_name:e.target.value}))} className="input-cozy" data-testid="host-last-name"/></div>

        <div><label className="label">Gender</label>
          <select required value={f.gender} onChange={e=>setF(s=>({...s, gender:e.target.value}))} className="input-cozy" data-testid="host-gender">
            <option value="">Select Option</option>
            {GENDERS.map((gender) => <option key={gender}>{gender}</option>)}
          </select>
        </div>
        <div><label className="label">Date of Birth</label><input required type="date" value={f.date_of_birth} onChange={e=>setF(s=>({...s, date_of_birth:e.target.value}))} className="input-cozy" data-testid="host-dob"/></div>

        <div><label className="label">Phone Number</label>
          <div className="grid grid-cols-[155px_minmax(0,1fr)] gap-3 max-w-xl">
            <PhoneCodeSelect value={f.phone_code} onChange={e=>setF(s=>({...s, phone_code:e.target.value}))} className="input-cozy" />
            <input required placeholder="Phone number" value={f.phone} onChange={e=>setF(s=>({...s, phone:e.target.value}))} className="input-cozy" data-testid="host-phone"/>
          </div>
        </div>
        <div><label className="label">City</label>
          <input ref={cityInputRef} required list="ghostie-city-suggestions" placeholder="Search for a city..." value={f.city} onChange={e=>setF(s=>({...s, city:e.target.value}))} className="input-cozy" data-testid="host-city-google"/>
          <datalist id="ghostie-city-suggestions">
            {citySuggestions.map((city) => <option key={city} value={city} />)}
          </datalist>
        </div>

        <div><label className="label">How long have you been living in the city?</label>
          <div className="flex">
            <input required min="0" type="number" value={f.living_length} onChange={e=>setF(s=>({...s, living_length:e.target.value}))} className="input-cozy rounded-r-none" data-testid="host-living-length"/>
            <select value={f.living_unit} onChange={e=>setF(s=>({...s, living_unit:e.target.value}))} className="input-cozy rounded-l-none border-l-0 max-w-[140px]">
              <option>Years</option>
              <option>Months</option>
            </select>
          </div>
        </div>
        <div><label className="label">Language</label>
          <select required value={f.language} onChange={e=>setF(s=>({...s, language:e.target.value}))} className="input-cozy" data-testid="host-language">
            {LANGUAGES.map((language) => <option key={language}>{language}</option>)}
          </select>
        </div>
        <div><label className="label">Level</label>
          <select required value={f.level} onChange={e=>setF(s=>({...s, level:e.target.value}))} className="input-cozy" data-testid="host-level">
            {LEVELS.map((level) => <option key={level}>{level}</option>)}
          </select>
        </div>
      </div>

      <section className="mt-8 rounded-3xl border border-[#3f2e1e] bg-[#111]/45 p-5 sm:p-6">
        <div className="badge-cozy mb-4">Email and password</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="label">Email Address</label><input required type="email" placeholder="your.email@example.com" value={f.email} onChange={e=>setF(s=>({...s, email:e.target.value}))} className="input-cozy" data-testid="host-email"/></div>
          <div><label className="label">Password</label><input type="password" minLength="8" placeholder="Create a password" value={f.password} onChange={e=>setF(s=>({...s, password:e.target.value}))} className="input-cozy" data-testid="host-password"/></div>
        </div>

        <label className="mt-4 flex items-start gap-3 text-sm text-[#c7c7c7]">
          <input required type="checkbox" checked={f.accepted_terms} onChange={e=>setF(s=>({...s, accepted_terms:e.target.checked}))} className="mt-1 accent-[#f97316]" data-testid="host-terms"/>
          <span>Accetto <a href="/legal/terms" className="text-[#f97316] hover:underline">termini e condizioni</a>.</span>
        </label>

        <button type="submit" disabled={submitting} className="btn-primary w-full justify-center mt-5">
          <Sparkles size={16}/> {submitting ? 'Submitting...' : 'Submit application'}
        </button>

        <div className="my-5 flex items-center gap-3 text-xs text-[#a3a3a3]">
          <span className="h-px bg-[#3f2e1e] flex-1" /> OR <span className="h-px bg-[#3f2e1e] flex-1" />
        </div>

        <div className="badge-cozy mb-4">Connect a Google account</div>
        <div className="flex justify-center"><div ref={googleButtonRef} /></div>
      </section>
    </form>
  );
}
